// Thin, defensive wrapper over @telegram-apps/sdk-react. Every call is safe outside Telegram
// (dev, e2e, a browser tab) and on old clients that lack a method.
import {
  backButton,
  hapticFeedback,
  init,
  isTMA,
  miniApp,
  mockTelegramEnv,
  openLink as sdkOpenLink,
  openTelegramLink as sdkOpenTelegramLink,
  retrieveRawInitData,
  swipeBehavior,
  themeParams,
  viewport,
} from '@telegram-apps/sdk-react';
import { MOCK_TELEGRAM } from '../env.ts';

type Theme = 'light' | 'dark';

const BG: Record<Theme, `#${string}`> = { light: '#F2F2F7', dark: '#000000' };

let available = false;
const themeListeners = new Set<(theme: Theme) => void>();

function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

/** Dev/e2e outside Telegram: a fake launch with `hash=dev` (the API accepts it only in dev). */
function mockLaunch(): void {
  const user = {
    id: Number(localStorage.getItem('dev-tg-id') ?? 1000001),
    first_name: 'Malika',
    language_code: 'uz',
  };
  const initData = new URLSearchParams({
    auth_date: String(Math.floor(Date.now() / 1000)),
    user: JSON.stringify(user),
    // Real clients always send `signature` (Ed25519 for third-party validation); the SDK requires it.
    signature: 'dev',
    hash: 'dev',
  });
  const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  mockTelegramEnv({
    launchParams: new URLSearchParams({
      tgWebAppData: initData.toString(),
      tgWebAppVersion: '8.0',
      tgWebAppPlatform: 'tdesktop',
      tgWebAppThemeParams: JSON.stringify({ bg_color: dark ? BG.dark : BG.light }),
    }),
  });
}

export function currentTheme(): Theme {
  const dark = available ? safe(() => miniApp.isDark()) : undefined;
  return (dark ?? window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  if (!available) return;
  safe(() => miniApp.setHeaderColor(BG[theme]));
  safe(() => miniApp.setBackgroundColor(BG[theme]));
  safe(() => miniApp.setBottomBarColor(BG[theme]));
}

/** Call once before rendering. Returns false when running outside Telegram without a mock. */
export function initTelegram(): boolean {
  if (!safe(() => isTMA()) && MOCK_TELEGRAM) mockLaunch();
  available =
    safe(() => {
      init();
      return true;
    }) ?? false;

  if (available) {
    safe(() => backButton.mount());
    safe(() => swipeBehavior.mount());
    // Sheets are dragged down to close; don't let that gesture minimize the Mini App.
    safe(() => swipeBehavior.disableVertical());
    safe(() => themeParams.mountSync());
    safe(() => miniApp.mountSync());
    // Viewport mounting is async (asks the client for sizes and safe areas).
    void safe(() =>
      viewport.mount().then(
        () => {
          safe(() => viewport.bindCssVars());
          safe(() => viewport.expand());
        },
        () => undefined,
      ),
    );
    safe(() =>
      miniApp.isDark.sub(() => {
        const theme = currentTheme();
        applyTheme(theme);
        themeListeners.forEach((l) => {
          l(theme);
        });
      }),
    );
  } else {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      applyTheme(currentTheme());
    });
  }
  applyTheme(currentTheme());
  return available;
}

/** Tell Telegram the first screen is ready (hides its loading placeholder). */
export function ready(): void {
  if (available) safe(() => miniApp.ready());
}

export function rawInitData(): string | undefined {
  return safe(() => retrieveRawInitData()) ?? undefined;
}

export function onThemeChange(listener: (theme: Theme) => void): () => void {
  themeListeners.add(listener);
  return () => themeListeners.delete(listener);
}

// ── Back button: shown while any sheet/screen is open (see state/layers.ts) ──
let backHandler: (() => void) | null = null;
let backUnsub: (() => void) | undefined;

export function setBackButton(handler: (() => void) | null): void {
  backHandler = handler;
  if (!available) return;
  if (handler) {
    backUnsub ??= safe(() =>
      backButton.onClick(() => {
        backHandler?.();
      }),
    );
    safe(() => backButton.show());
  } else {
    safe(() => backButton.hide());
  }
}

// ── Haptics (design/Main.dc.html · Motion · Haptics) ──
export const haptic = {
  /** play / pause */
  tap: () => available && safe(() => hapticFeedback.impactOccurred('light')),
  /** plan select, language switch, timer option */
  select: () => available && safe(() => hapticFeedback.selectionChanged()),
  /** payment success */
  success: () => available && safe(() => hapticFeedback.notificationOccurred('success')),
  error: () => available && safe(() => hapticFeedback.notificationOccurred('error')),
};

export function openLink(url: string): void {
  if (!available || safe(() => sdkOpenLink(url)) === undefined)
    window.open(url, '_blank', 'noopener');
}

export function openTelegramLink(url: string): void {
  if (!available || safe(() => sdkOpenTelegramLink(url)) === undefined)
    window.open(url, '_blank', 'noopener');
}

/** Telegram share dialog (chat picker) for a link + caption. */
export function shareLink(url: string, text: string): void {
  const q = new URLSearchParams({ url, text });
  openTelegramLink(`https://t.me/share/url?${q.toString()}`);
}

/** Closes the Mini App (used by the "reopen the app" screen). */
export function closeApp(): void {
  if (available && safe(() => miniApp.close()) !== undefined) return;
  window.close();
}

export const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
